import { contactsExportType } from '../const/contactsExportType.js'
import { ExportMetadataFactory } from '../export/exportMetadataFactory.js'
import { EmptySearchPageError } from '../common/errors/EmptySearchPageError.js'
import { utils } from '../common/utils.js'
import { getContactsFromSalesNavigatorSearch } from '../linkedInExportApi.js'
import type {
	ContactsIteratorResult,
	ExportMetadataItem,
	IBroker
} from './types.js'

interface SearchRequest {
	queryParameters: Record<string, string>
	start: number
	pageSize: number
}

export class SalesNavigatorSearchPageIterator {
	readonly #broker: IBroker
	#canNext: boolean
	readonly #searchRequest: SearchRequest
	#total: number | undefined
	readonly #pageSize = 25
	readonly #searchPageUri: URL
	readonly #metadata: ExportMetadataItem[]

	constructor(broker: IBroker, uri: URL) {
		this.#broker = broker
		this.#searchPageUri = uri
		this.#metadata = ExportMetadataFactory.create(uri)
		const search = uri.hash ? uri.hash.substring(1) : uri.search
		const searchParameters = ExportMetadataFactory.getSearchParamsDictionary(
			search
		) as Record<string, string>
		this.#searchRequest = this.#parseSearchRequest(searchParameters)
		this.#canNext = this.#isNotReachedPageLimit()
	}

	async getNext(metadata?: object): Promise<ContactsIteratorResult> {
		const referrer = utils.getReferrer(this.#searchPageUri)
		const response = (await getContactsFromSalesNavigatorSearch(
			this.#broker,
			this.#searchRequest,
			referrer,
			metadata
		)) as {
			paging: { total: number; start: number; count: number }
			contacts: unknown[]
		}

		const isNotAllContactsProcessed =
			response.paging.total > response.paging.start + response.paging.count
		this.#canNext = isNotAllContactsProcessed
		if (this.#canNext) {
			this.#searchRequest.start += this.#pageSize
			this.#canNext = this.#isNotReachedPageLimit()
		}

		if (this.#total === undefined) {
			if (response.paging.total === 0) {
				throw new EmptySearchPageError(
					`there is no contacts on the search page ${this.#searchPageUri}`,
					metadata
				)
			}
			this.#total = response.paging.total
		}

		const result = {
			found: response.contacts,
			processed: isNotAllContactsProcessed
				? response.paging.count
				: response.paging.total - response.paging.start
		}
		return result
	}

	canMove(): boolean {
		return this.#canNext
	}

	getTotal(): number {
		return this.#total ?? 0
	}

	getMetadata(): ExportMetadataItem[] {
		return this.#metadata
	}

	getType(): string {
		return contactsExportType.salesNavigatorContactsSearchPage
	}

	#parseSearchRequest(queryParameters: Record<string, string>): SearchRequest {
		let start = 0
		if (queryParameters.page) {
			const originalPageNumber = queryParameters.page
			const pageNumber = parseInt(originalPageNumber)
			start = (pageNumber - 1) * this.#pageSize
			delete queryParameters.page
		}

		const result = {
			queryParameters: queryParameters,
			start: start,
			pageSize: this.#pageSize
		}
		return result
	}

	#isNotReachedPageLimit(): boolean {
		const result = this.#searchRequest.start / this.#pageSize < 100
		return result
	}
}
